import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useATMStore } from '../store/atmStore'
import type { ATMDevice, ATMEvent, ATMModel, ATMStatus, ATMZone, EventSeverity } from '@atmvision/types'
import { BarChart, Bar, Cell, ResponsiveContainer } from 'recharts'
import { modelIcon, modelInfocard } from '../modelImages'
import { MapContainer, TileLayer, Marker } from 'react-leaflet'
import L from 'leaflet'
import { useTheme } from '../hooks/useTheme'

const atmPinIcon = L.icon({
  iconUrl: '/banreservas-pin.png',
  iconSize: [46, 56],
  iconAnchor: [23, 56],
  popupAnchor: [0, -56],
})

const STATUS_COLOR: Record<ATMStatus, string> = {
  IN_SERVICE: '#22c55e', OUT_OF_SERVICE: '#ef4444', WARNING: '#F89421',
  SUPERVISOR: '#02AFF0', LOADING: '#facc15', MAINTENANCE: '#8b5cf6', UNKNOWN: '#6b7280',
}

const STATUS_LABEL: Record<ATMStatus, string> = {
  IN_SERVICE: 'En Servicio', OUT_OF_SERVICE: 'Fuera', WARNING: 'Advertencia',
  SUPERVISOR: 'Supervisor', LOADING: 'Cargando', MAINTENANCE: 'Mantenimiento', UNKNOWN: 'Desconocido',
}

const PAGE_SIZE = 50

type SortKey = 'terminal_id' | 'model' | 'zone' | 'status' | 'so_image' | 'last_seen'
type SortDir = 'asc' | 'desc'

const ZONES: Array<ATMZone | 'ALL'> = ['ALL', 'Distrito Nacional', 'Norte', 'Sur', 'Este']
const STATUSES: Array<ATMStatus | 'ALL'> = ['ALL', 'IN_SERVICE', 'OUT_OF_SERVICE', 'WARNING', 'SUPERVISOR', 'UNKNOWN']

export default function FleetGrid() {
  const C = useTheme()
  const { fleet, events } = useATMStore()
  const [search, setSearch] = useState('')
  const [zoneFilter, setZoneFilter] = useState<ATMZone | 'ALL'>('ALL')
  const [statusFilter, setStatusFilter] = useState<ATMStatus | 'ALL'>('ALL')
  const [sortKey, setSortKey] = useState<SortKey>('terminal_id')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [page, setPage] = useState(0)
  const [selected, setSelected] = useState<ATMDevice | null>(null)
  const [modal, setModal] = useState<ATMDevice | null>(null)

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    return fleet
      .filter((a) => {
        if (q && !a.terminal_id.toLowerCase().includes(q) && !a.address.toLowerCase().includes(q)) return false
        if (zoneFilter !== 'ALL' && a.zone !== zoneFilter) return false
        if (statusFilter !== 'ALL' && a.status !== statusFilter) return false
        return true
      })
      .sort((a, b) => {
        let av = (a[sortKey] ?? '') as string
        let bv = (b[sortKey] ?? '') as string
        if (sortKey === 'last_seen') return sortDir === 'asc'
          ? new Date(av).getTime() - new Date(bv).getTime()
          : new Date(bv).getTime() - new Date(av).getTime()
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
      })
  }, [fleet, search, zoneFilter, statusFilter, sortKey, sortDir])

  const pages = Math.ceil(filtered.length / PAGE_SIZE)
  const currentPage = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  const handleSort = useCallback((key: SortKey) => {
    setSortKey((k) => {
      if (k === key) { setSortDir((d) => d === 'asc' ? 'desc' : 'asc'); return k }
      setSortDir('asc')
      return key
    })
    setPage(0)
  }, [])

  const exportCSV = useCallback(() => {
    const header = ['terminal_id', 'model', 'zone', 'province', 'municipality', 'address', 'status', 'so_image', 'brigada', 'last_seen']
    const rows = filtered.map((a) => header.map((k) => {
      const v = (a as unknown as Record<string, unknown>)[k]
      return typeof v === 'string' && v.includes(',') ? `"${v}"` : v ?? ''
    }).join(','))
    const csv = [header.join(','), ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `fleet_export_${new Date().toISOString().slice(0, 10)}.csv`
    a.click(); URL.revokeObjectURL(url)
  }, [filtered])

  const SortIcon = ({ k }: { k: SortKey }) => (
    <span style={{ marginLeft: 4, opacity: sortKey === k ? 1 : 0.3, fontSize: 10 }}>
      {sortKey === k && sortDir === 'desc' ? '▼' : '▲'}
    </span>
  )

  return (
    <div style={{ flex: 1, display: 'flex', overflow: 'hidden', background: C.dark }}>
      {/* Main table area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Toolbar */}
        <div style={{ padding: '10px 16px', background: C.card, borderBottom: `1px solid ${C.border}`, display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0) }}
            placeholder="Buscar por terminal ID o dirección..."
            style={{ background: C.card2, border: `1px solid ${C.border}`, color: C.text, borderRadius: 6, padding: '6px 10px', fontSize: 12, fontFamily: 'inherit', width: 260 }}
          />
          <select value={zoneFilter} onChange={(e) => { setZoneFilter(e.target.value as ATMZone | 'ALL'); setPage(0) }}
            style={{ background: C.card2, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 8px', fontSize: 12, fontFamily: 'inherit' }}>
            {ZONES.map((z) => <option key={z} value={z}>{z === 'ALL' ? 'Todas las Zonas' : z}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value as ATMStatus | 'ALL'); setPage(0) }}
            style={{ background: C.card2, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 8px', fontSize: 12, fontFamily: 'inherit' }}>
            {STATUSES.map((s) => <option key={s} value={s}>{s === 'ALL' ? 'Todos los Estados' : STATUS_LABEL[s as ATMStatus]}</option>)}
          </select>

          <span style={{ color: C.muted, fontSize: 12, marginLeft: 8 }}>
            {filtered.length} de {fleet.length} ATMs
          </span>

          <button onClick={exportCSV} style={{ marginLeft: 'auto', background: 'rgba(2,175,240,0.12)', border: '1px solid rgba(2,175,240,0.4)', color: C.blue, padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>
            Exportar CSV
          </button>
        </div>

        {/* Table */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead style={{ position: 'sticky', top: 0, background: C.card2, zIndex: 1 }}>
              <tr>
                {([
                  ['terminal_id', 'Terminal ID'],
                  ['model', 'Modelo'],
                  ['zone', 'Zona'],
                  ['status', 'Estado'],
                  ['so_image', 'Imagen SO'],
                  ['last_seen', 'Última señal'],
                ] as [SortKey, string][]).map(([key, label]) => (
                  <th
                    key={key}
                    onClick={() => handleSort(key)}
                    style={{
                      padding: '10px 12px', textAlign: 'left', cursor: 'pointer',
                      color: sortKey === key ? C.blue : C.muted,
                      borderBottom: `1px solid ${C.border}`, fontWeight: 600,
                      textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10,
                      userSelect: 'none', whiteSpace: 'nowrap',
                    }}
                  >
                    {label}<SortIcon k={key} />
                  </th>
                ))}
                <th style={{ padding: '10px 12px', color: C.muted, borderBottom: `1px solid ${C.border}`, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>
                  Brigada
                </th>
                <th style={{ padding: '10px 12px', color: C.muted, borderBottom: `1px solid ${C.border}`, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10, width: 80 }}>
                  Detalle
                </th>
              </tr>
            </thead>
            <tbody>
              {currentPage.map((atm, i) => (
                <tr
                  key={atm.terminal_id}
                  onClick={() => setSelected(atm)}
                  style={{
                    background: selected?.terminal_id === atm.terminal_id ? 'rgba(38,78,114,0.3)' : i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)',
                    borderBottom: `1px solid ${C.border}`,
                    cursor: 'pointer',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={(e) => { if (selected?.terminal_id !== atm.terminal_id) (e.currentTarget as HTMLTableRowElement).style.background = 'rgba(255,255,255,0.04)' }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = selected?.terminal_id === atm.terminal_id ? 'rgba(38,78,114,0.3)' : i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}
                >
                  <td style={{ padding: '9px 12px', fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, color: C.text }}>
                    {atm.terminal_id}
                  </td>
                  <td style={{ padding: '6px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <img src={modelIcon(atm.model as ATMModel)} alt="" style={{ height: 28, width: 'auto', opacity: 0.9, flexShrink: 0 }} />
                      <span style={{ color: C.muted, fontSize: 11 }}>{atm.model}</span>
                    </div>
                  </td>
                  <td style={{ padding: '9px 12px', color: C.muted }}>{atm.zone}</td>
                  <td style={{ padding: '9px 12px' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '2px 8px', borderRadius: 999,
                      background: `${STATUS_COLOR[atm.status]}18`,
                      color: STATUS_COLOR[atm.status], fontSize: 11, fontWeight: 600,
                    }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: STATUS_COLOR[atm.status] }} />
                      {STATUS_LABEL[atm.status]}
                    </span>
                  </td>
                  <td style={{ padding: '9px 12px', fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>
                    {atm.so_image ? (
                      <span style={{ color: C.blue }}>{atm.so_image}</span>
                    ) : (
                      <span style={{ color: C.muted, fontStyle: 'italic' }}>Sin imagen</span>
                    )}
                  </td>
                  <td style={{ padding: '9px 12px', color: C.muted, fontSize: 11 }}>
                    {new Date(atm.last_seen).toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td style={{ padding: '9px 12px', color: C.muted, fontSize: 11 }}>{atm.brigada}</td>
                  <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); setModal(atm) }}
                      title="Ver detalle completo"
                      style={{ background: 'rgba(2,175,240,0.15)', border: '1px solid rgba(2,175,240,0.5)', color: C.blue, cursor: 'pointer', borderRadius: 4, padding: '4px 10px', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', letterSpacing: 0.3 }}
                    >
                      Ver +
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div style={{ padding: '10px 16px', background: C.card, borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: 12, color: C.muted }}>
              Página {page + 1} de {pages} ({PAGE_SIZE} por página)
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setPage(0)} disabled={page === 0} style={pageBtn(page === 0, false, C)}>«</button>
              <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} style={pageBtn(page === 0, false, C)}>‹</button>
              {Array.from({ length: Math.min(5, pages) }, (_, i) => {
                const p = Math.max(0, Math.min(pages - 5, page - 2)) + i
                return (
                  <button key={p} onClick={() => setPage(p)} style={pageBtn(false, p === page, C)}>
                    {p + 1}
                  </button>
                )
              })}
              <button onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1} style={pageBtn(page >= pages - 1, false, C)}>›</button>
              <button onClick={() => setPage(pages - 1)} disabled={page >= pages - 1} style={pageBtn(page >= pages - 1, false, C)}>»</button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selected && (
        <ATMDetailPanel atm={selected} onClose={() => setSelected(null)} />
      )}

      {/* Full-screen portal modal */}
      {modal && (
        <ATMModal atm={modal} events={events} onClose={() => setModal(null)} />
      )}
    </div>
  )
}

function pageBtn(disabled: boolean, active = false, C?: ReturnType<typeof useTheme>): React.CSSProperties {
  const primary = C?.primary ?? '#264E72'
  const blue = C?.blue ?? '#02AFF0'
  const border = C?.border ?? 'rgba(255,255,255,0.08)'
  const text = C?.text ?? '#e7ecf3'
  return {
    background: active ? primary : 'rgba(255,255,255,0.05)',
    border: `1px solid ${active ? blue : border}`,
    color: disabled ? '#4a5568' : text, padding: '4px 10px',
    borderRadius: 5, cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 12, fontFamily: 'inherit',
  }
}

function ATMDetailPanel({ atm, onClose }: { atm: ATMDevice; onClose: () => void }) {
  const C = useTheme()
  const cassetteData = (atm.cashCassettes ?? []).map((c) => {
    const curr = c.current ?? (c as unknown as Record<string, number>).current_count ?? 0
    const cap  = c.capacity || 1
    const ratio = curr / cap
    return {
      name:  `${c.id}\nRD$${c.denomination}`,
      pct:   Math.round(ratio * 100),
      color: ratio < 0.15 ? '#ef4444' : ratio < 0.3 ? '#F89421' : '#22c55e',
    }
  })

  return (
    <div style={{
      width: 320, background: C.card, borderLeft: `1px solid ${C.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'auto', flexShrink: 0,
    }}>
      {/* Infocard image header */}
      {(() => {
        const infocard = modelInfocard(atm.model as ATMModel)
        return (
          <div style={{ position: 'relative', borderBottom: `1px solid ${C.border}`, background: '#101820', flexShrink: 0 }}>
            <img src={infocard} alt={atm.model} style={{ width: '100%', display: 'block', maxHeight: 220, objectFit: 'contain', padding: '12px 16px' }} />
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(10,18,28,0.95))', padding: '24px 14px 10px' }}>
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 15, color: C.text }}>{atm.terminal_id}</div>
              <div style={{ fontSize: 11, color: C.blue, marginTop: 1 }}>{atm.model}</div>
            </div>
            <button onClick={onClose} style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.5)', border: `1px solid ${C.border}`, color: C.muted, cursor: 'pointer', fontSize: 14, lineHeight: 1, borderRadius: 4, padding: '2px 6px' }}>✕</button>
          </div>
        )
      })()}

      <div style={{ padding: '12px 14px', flex: 1 }}>
        <Section title="Ubicación">
          <KV pairs={[
            ['Zona', atm.zone], ['Provincia', atm.province],
            ['Municipio', atm.municipality], ['Localidad', atm.localidad],
            ['Brigada', atm.brigada],
          ]} />
          <div style={{ fontSize: 11, color: C.muted, marginTop: 6, fontStyle: 'italic' }}>{atm.address}</div>
        </Section>

        <Section title="Equipamiento">
          <KV pairs={[
            ['Familia', atm.family], ['Acceso', atm.acceso],
            ['Lector', atm.card_reader], ['Funcionalidad', atm.funcionalidad],
            ['Imagen SO', atm.so_image ?? 'Sin asignar'],
            ['Serie', atm.serial_number],
          ]} />
        </Section>

        <Section title="Estado">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: STATUS_COLOR[atm.status] }} />
            <span style={{ color: STATUS_COLOR[atm.status], fontWeight: 600, fontSize: 13 }}>
              {STATUS_LABEL[atm.status]}
            </span>
          </div>
          <div style={{ fontSize: 11, color: C.muted }}>
            Última señal: {new Date(atm.last_seen).toLocaleString('es-DO')}
          </div>
        </Section>

        <Section title="Cassettes de Efectivo">
          <ResponsiveContainer width="100%" height={110}>
            <BarChart data={cassetteData} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
              <Bar dataKey="pct" radius={[3, 3, 0, 0]}>
                {cassetteData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          {(atm.cashCassettes ?? []).map((c) => {
            const curr = c.current ?? (c as unknown as Record<string, number>).current_count ?? 0
            const cap  = c.capacity || 1
            const pct  = Math.round(curr / cap * 100)
            const color = pct < 15 ? '#ef4444' : pct < 30 ? '#F89421' : '#22c55e'
            return (
              <div key={c.id} style={{ marginBottom: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 2 }}>
                  <span style={{ color: C.muted }}>{c.id} — RD${c.denomination}</span>
                  <span style={{ color }}>{curr}/{cap} ({pct}%)</span>
                </div>
                <div style={{ height: 4, background: 'rgba(255,255,255,0.08)', borderRadius: 2 }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 2 }} />
                </div>
              </div>
            )
          })}
        </Section>

        <Section title="Componentes">
          {atm.devices.map((d) => (
            <div key={d.component} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5, fontSize: 12 }}>
              <span style={{ color: C.muted, fontFamily: 'JetBrains Mono, monospace' }}>{d.component}</span>
              <span style={{
                padding: '1px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                background: d.status === 'OK' ? 'rgba(34,197,94,0.15)' : d.status === 'FAULT' ? 'rgba(239,68,68,0.15)' : d.status === 'WARNING' ? 'rgba(248,148,33,0.15)' : 'rgba(107,114,128,0.15)',
                color: d.status === 'OK' ? '#22c55e' : d.status === 'FAULT' ? '#ef4444' : d.status === 'WARNING' ? '#F89421' : '#6b7280',
              }}>
                {d.status}{d.last_error ? ` — ${d.last_error}` : ''}
              </span>
            </div>
          ))}
        </Section>
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  const C = useTheme()
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 10, color: C.blue, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 8 }}>
        {title}
      </div>
      {children}
    </div>
  )
}

function KV({ pairs }: { pairs: [string, string][] }) {
  const C = useTheme()
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: '3px 8px', fontSize: 11 }}>
      {pairs.map(([k, v]) => (
        <>
          <span key={k + '_k'} style={{ color: C.muted }}>{k}</span>
          <span key={k + '_v'} style={{ color: C.text, wordBreak: 'break-word' }}>{v}</span>
        </>
      ))}
    </div>
  )
}

const SEV_COLOR: Record<EventSeverity, string> = {
  CRITICAL: '#ef4444', HIGH: '#F89421', WARNING: '#facc15', INFO: '#02AFF0', OK: '#22c55e',
}

const SIM_API = import.meta.env.VITE_SIM_API || 'http://localhost:8001'

function ATMModal({ atm, events, onClose }: { atm: ATMDevice; events: ATMEvent[]; onClose: () => void }) {
  const C = useTheme()
  const [history, setHistory] = useState<ATMEvent[]>([])
  const [loadingHistory, setLoadingHistory] = useState(true)

  useEffect(() => {
    fetch(`${SIM_API}/api/v1/events/history?term_id=${atm.terminal_id}&limit=200`)
      .then((r) => r.json())
      .then((rows: ATMEvent[]) => { setHistory(rows); setLoadingHistory(false) })
      .catch(() => setLoadingHistory(false))
  }, [atm.terminal_id])

  const allEvents = useMemo(() => {
    const live = events.filter((e) => e.term_id === atm.terminal_id)
    const seen = new Set<string>()
    return [...live, ...history]
      .filter((e) => { if (seen.has(e.event_id)) return false; seen.add(e.event_id); return true })
      .sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime())
      .slice(0, 300)
  }, [events, history, atm.terminal_id])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const ext = atm as unknown as Record<string, unknown>

  return createPortal(
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        style={{ background: C.card, borderRadius: 10, border: `1px solid ${C.border}`, width: '88vw', maxWidth: 1120, height: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 80px rgba(0,0,0,0.7)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div style={{ position: 'relative', background: '#0d1620', flexShrink: 0, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'flex-end', minHeight: 110 }}>
          <img
            src={modelInfocard(atm.model as ATMModel)}
            alt={atm.model}
            style={{ height: 110, width: 'auto', objectFit: 'contain', padding: '10px 16px', flexShrink: 0 }}
          />
          <div style={{ flex: 1, padding: '12px 16px 14px' }}>
            <div style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 18, color: C.text }}>{atm.terminal_id}</div>
            <div style={{ fontSize: 12, color: C.blue, marginTop: 2 }}>{atm.model}</div>
            <div style={{ marginTop: 6, display: 'flex', gap: 10, alignItems: 'center' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 999, background: `${STATUS_COLOR[atm.status]}18`, color: STATUS_COLOR[atm.status], fontSize: 12, fontWeight: 600 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: STATUS_COLOR[atm.status] }} />
                {STATUS_LABEL[atm.status]}
              </span>
              <span style={{ fontSize: 11, color: C.muted }}>
                {new Date(atm.last_seen).toLocaleString('es-DO')}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ position: 'absolute', top: 10, right: 12, background: 'rgba(0,0,0,0.5)', border: `1px solid ${C.border}`, color: C.muted, cursor: 'pointer', fontSize: 16, lineHeight: 1, borderRadius: 5, padding: '4px 9px' }}
          >
            ✕
          </button>
        </div>

        {/* Modal body */}
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

          {/* Left: extended info */}
          <div style={{ width: 360, borderRight: `1px solid ${C.border}`, overflow: 'auto', padding: '14px 16px', flexShrink: 0 }}>
            <Section title="Identificación">
              <KV pairs={[
                ['Unidad', String(ext.unit ?? '—')],
                ['Terminal ID', atm.terminal_id],
                ['Nombre ATM', String(ext.atm_name ?? '—')],
                ['Serie', atm.serial_number],
              ]} />
            </Section>

            <Section title="Ubicación">
              <KV pairs={[
                ['Zona', atm.zone],
                ['Provincia', atm.province],
                ['Municipio', atm.municipality],
                ['Localidad', atm.localidad],
                ['Gpo. Atención', String(ext.grupo_atencion ?? '—')],
                ['Brigada', atm.brigada],
                ['Zona Turística', ext.zona_turistica ? 'Sí' : 'No'],
              ]} />
              <div style={{ fontSize: 11, color: C.muted, marginTop: 6, fontStyle: 'italic' }}>{atm.address}</div>
            </Section>

            <div style={{ height: 170, borderRadius: 7, overflow: 'hidden', border: `1px solid ${C.border}`, marginBottom: 16 }}>
              <MapContainer
                key={atm.terminal_id + '-minimap'}
                center={[atm.lat, atm.lon]}
                zoom={15}
                zoomControl={false}
                scrollWheelZoom={false}
                dragging={false}
                doubleClickZoom={false}
                attributionControl={false}
                style={{ width: '100%', height: '100%' }}
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={19} />
                <Marker position={[atm.lat, atm.lon]} icon={atmPinIcon} />
              </MapContainer>
            </div>

            <Section title="Equipamiento">
              <KV pairs={[
                ['Modelo', atm.model],
                ['Familia', atm.family],
                ['Acceso', atm.acceso],
                ['Posición', String(ext.posicion ?? '—')],
                ['Lector', atm.card_reader],
                ['Funcionalidad', atm.funcionalidad],
                ['Imagen SO', atm.so_image ?? 'Sin asignar'],
                ['Conectividad', String(ext.connectivity ?? '—')],
              ]} />
            </Section>

            <Section title="Operación">
              <KV pairs={[
                ['Institución', String(atm.institucion ?? '—')],
                ['Cod. Oficina', String(ext.cod_oficina ?? '—')],
                ['Oficina', String(ext.oficina ?? '—')],
              ]} />
            </Section>

            <Section title="Cassettes">
              {(atm.cashCassettes ?? []).map((c) => {
                const curr = c.current ?? 0
                const cap = c.capacity || 1
                const pct = Math.round(curr / cap * 100)
                const col = pct < 15 ? '#ef4444' : pct < 30 ? '#F89421' : '#22c55e'
                return (
                  <div key={c.id} style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}>
                      <span style={{ color: C.muted }}>{c.id} — RD${c.denomination}</span>
                      <span style={{ color: col }}>{curr.toLocaleString()}/{cap.toLocaleString()} ({pct}%)</span>
                    </div>
                    <div style={{ height: 5, background: 'rgba(255,255,255,0.08)', borderRadius: 3 }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: col, borderRadius: 3, transition: 'width 0.3s' }} />
                    </div>
                  </div>
                )
              })}
            </Section>

            <Section title="Componentes">
              {atm.devices.map((d) => (
                <div key={d.component} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5, fontSize: 12 }}>
                  <span style={{ color: C.muted, fontFamily: 'JetBrains Mono, monospace' }}>{d.component}</span>
                  <span style={{
                    padding: '1px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                    background: d.status === 'OK' ? 'rgba(34,197,94,0.15)' : d.status === 'FAULT' ? 'rgba(239,68,68,0.15)' : d.status === 'WARNING' ? 'rgba(248,148,33,0.15)' : 'rgba(107,114,128,0.15)',
                    color: d.status === 'OK' ? '#22c55e' : d.status === 'FAULT' ? '#ef4444' : d.status === 'WARNING' ? '#F89421' : '#6b7280',
                  }}>
                    {d.status}
                  </span>
                </div>
              ))}
            </Section>
          </div>

          {/* Right: live event feed */}
          <div style={{ flex: 1, overflow: 'auto', padding: '14px 16px' }}>
            <div style={{ fontSize: 10, color: C.blue, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
              Historial de Eventos — {atm.terminal_id}
              <span style={{ color: C.muted, fontWeight: 400 }}>({allEvents.length})</span>
              {loadingHistory && <span style={{ color: C.muted, fontStyle: 'italic', fontSize: 10, fontWeight: 400 }}>cargando...</span>}
            </div>
            {allEvents.length === 0 ? (
              <div style={{ color: C.muted, fontSize: 12, fontStyle: 'italic', textAlign: 'center', marginTop: 40 }}>
                {loadingHistory ? 'Cargando historial...' : 'Sin eventos para este terminal'}
              </div>
            ) : allEvents.map((ev) => (
              <div key={ev.event_id} style={{ marginBottom: 8, padding: '10px 12px', background: C.card2, borderRadius: 6, borderLeft: `3px solid ${SEV_COLOR[ev.severity]}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ color: SEV_COLOR[ev.severity], fontWeight: 700, fontSize: 11 }}>{ev.severity}</span>
                  <span style={{ color: C.muted, fontSize: 10, fontFamily: 'JetBrains Mono, monospace' }}>
                    {new Date(ev.ts).toLocaleString('es-DO')}
                  </span>
                </div>
                <div style={{ color: C.text, fontSize: 12, marginBottom: 5 }}>{ev.description}</div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10, color: C.muted, fontFamily: 'JetBrains Mono, monospace' }}>
                    raw: <span style={{ color: C.text }}>{ev.raw_code}</span>
                  </span>
                  {ev.canonical_code && (
                    <span style={{ fontSize: 10, color: C.muted, fontFamily: 'JetBrains Mono, monospace' }}>
                      canon: <span style={{ color: C.blue }}>{ev.canonical_code}</span>
                    </span>
                  )}
                  <span style={{ fontSize: 10, color: C.muted }}>{ev.event_type}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
